import { ref } from 'vue'

export interface AppEvent<T = any> {
  type: string
  data: T
}

export const useEventStore = defineStore('eventStore', () => {
  const events = ref<AppEvent[]>([])

  function setEvents(newEvents: AppEvent[]) {
    events.value = newEvents
  }

  function addEvent(event: AppEvent) {
    events.value.push(event)
  }

  function getEventsByType(type: string) {
    return events.value.filter((e) => e.type === type)
  }

  function getLatestEventByType(type: string) {
    return [...events.value].reverse().find((e) => e.type === type)
  }

  function extractCourseVersionIdFromEvent(event: AppEvent): string | null {
    let data = event.data

    // If data is a string, try to parse it
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data)
      } catch (e) {
        return null
      }
    }
    if (
      event.type === 'DELETE_REGIONAL_COURSE_VERSION' &&
      data &&
      typeof data.message === 'string'
    ) {
      const match = data.message.match(/curso regional (\d+)/)
      if (match && match[1]) return match[1]
    }

    return null
  }

  function getAllProcessingDeleteCourseVersionIds(): string[] {
    return events.value
      .filter((e) => {
        let data = e.data
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data)
          } catch (err) {
            return false
          }
        }

        return (
          e.type === 'DELETE_REGIONAL_COURSE_VERSION' &&
          data &&
          data.status === 'processing' &&
          typeof data.message === 'string'
        )
      })
      .map((e) => extractCourseVersionIdFromEvent(e))
      .filter((id): id is string => !!id)
  }

  function isImportingRegionalCourseVersions(): boolean {
    return events.value.some((e) => {
      if (e.type !== 'IMPORT_REGIONAL_COURSE_VERSION') return false
      let data = e.data
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data)
        } catch {
          return false
        }
      }
      return data?.status === 'processing'
    })
  }

  function getCoursePlanGenerationEvents(courseVersionId: string) {
    return events.value.filter((e) => {
      if (e.type !== 'COURSE_PLAN_GENERATION') return false
      let data = e.data
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data)
        } catch {
          return false
        }
      }
      return String(data?.courseVersionId) === String(courseVersionId)
    })
  }

  function removeEvent(event: AppEvent) {
    const idx = events.value.indexOf(event)
    if (idx !== -1) events.value.splice(idx, 1)
  }

  return {
    events,
    setEvents,
    addEvent,
    getEventsByType,
    getLatestEventByType,
    extractCourseVersionIdFromEvent,
    getAllProcessingDeleteCourseVersionIds,
    isImportingRegionalCourseVersions,
    getCoursePlanGenerationEvents,
    removeEvent,
  }
})
